<script setup lang="ts">
import type { Contact } from '@proj-airi/stage-ui/stores/contacts'

import { CallModal, GlobalConfirmDialog, IncomingCallDialog, OnboardingDialog, OutgoingCallDialog, ToasterRoot } from '@proj-airi/stage-ui/components'
import { STAGE_MODEL_UPDATE_DELAY } from '@proj-airi/stage-ui/constants/performance'
import { useBaiduSyncStore } from '@proj-airi/stage-ui/stores/baidu-sync'
import { useConfiguratorForAiriSdk } from '@proj-airi/stage-ui/stores/configurator'
import { useContactsStore } from '@proj-airi/stage-ui/stores/contacts'
import { useDisplayModelsStore } from '@proj-airi/stage-ui/stores/display-models'
import { useMemoryStore } from '@proj-airi/stage-ui/stores/memory'
import { useAiriCardStore } from '@proj-airi/stage-ui/stores/modules/airi-card'
import { useOnboardingStore } from '@proj-airi/stage-ui/stores/onboarding'
import { DISPLAY_MODEL_NONE, useSettings } from '@proj-airi/stage-ui/stores/settings'
import { ErrorBoundary } from '@proj-airi/ui'
import { StageTransitionGroup } from '@proj-airi/ui-transitions'
import { breakpointsTailwind, useBreakpoints, useDark } from '@vueuse/core'
import { storeToRefs } from 'pinia'
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { RouterView, useRoute, useRouter } from 'vue-router'
import { toast, Toaster } from 'vue-sonner'

import ActivationGate from './components/ActivationGate.vue'

import { useChatBubbleTheme } from './composables/chat-bubble-theme'
import { useCustomFont } from './composables/custom-font'
import { usePWAStore } from './stores/pwa'

import 'vue-sonner/style.css'

usePWAStore()
useChatBubbleTheme()
useCustomFont()

const i18n = useI18n()
const displayModelsStore = useDisplayModelsStore()
const settingsStore = useSettings()
const settings = storeToRefs(settingsStore)
const onboardingStore = useOnboardingStore()
const baiduSyncStore = useBaiduSyncStore()
const { shouldShowSetup } = storeToRefs(onboardingStore)
const { needsReauthorization } = storeToRefs(baiduSyncStore)
const isDark = useDark()
const breakpoints = useBreakpoints(breakpointsTailwind)
const isMobile = breakpoints.smaller('sm')
const { dispose } = useConfiguratorForAiriSdk()
const currentRoute = useRoute()
const router = useRouter()

function openBaiduSyncSettings() {
  void router.push('/settings/modules/baidu-sync')
}

const baiduReauthorizationToastId = 'baidu-reauthorization-required'
function syncBaiduReauthorizationToast(required: boolean) {
  if (!required) {
    toast.dismiss(baiduReauthorizationToastId)
    return
  }

  toast.warning('百度网盘授权已过期，自动备份和恢复已暂停', {
    id: baiduReauthorizationToastId,
    duration: 10_000,
    action: {
      label: '重新授权',
      onClick: () => {
        toast.dismiss(baiduReauthorizationToastId)
        openBaiduSyncSettings()
      },
    },
  })
}
watch(needsReauthorization, syncBaiduReauthorizationToast)

// 路由切换时，如果错误边界已捕获错误，则清除错误让用户离开出错页面时自动恢复。
// 注意：不能用 :key 绑定路由重建 ErrorBoundary，否则会连带销毁内部 KeepAlive 缓存。
const errorBoundaryRef = ref<InstanceType<typeof ErrorBoundary>>()
watch(() => currentRoute.path, () => {
  if (errorBoundaryRef.value?.hasError())
    errorBoundaryRef.value.retry()
})

// 监听联系人和角色切换，自动更新显示模型
const contactsStore = useContactsStore()
const airiCardStore = useAiriCardStore()
const { activeContact, activeContactId, storageError } = storeToRefs(contactsStore)
const { activeCard, cards } = storeToRefs(airiCardStore)
const { stageModelSelected } = storeToRefs(settingsStore)
const { displayModels } = storeToRefs(displayModelsStore)

const primaryColor = computed(() => {
  return isDark.value
    ? `color-mix(in srgb, oklch(95% var(--chromatic-chroma-900) calc(var(--chromatic-hue) + ${0})) 70%, oklch(50% 0 360))`
    : `color-mix(in srgb, oklch(95% var(--chromatic-chroma-900) calc(var(--chromatic-hue) + ${0})) 90%, oklch(90% 0 360))`
})

const secondaryColor = computed(() => {
  return isDark.value
    ? `color-mix(in srgb, oklch(95% var(--chromatic-chroma-900) calc(var(--chromatic-hue) + ${180})) 70%, oklch(50% 0 360))`
    : `color-mix(in srgb, oklch(95% var(--chromatic-chroma-900) calc(var(--chromatic-hue) + ${180})) 90%, oklch(90% 0 360))`
})

const tertiaryColor = computed(() => {
  return isDark.value
    ? `color-mix(in srgb, oklch(95% var(--chromatic-chroma-900) calc(var(--chromatic-hue) + ${60})) 70%, oklch(50% 0 360))`
    : `color-mix(in srgb, oklch(95% var(--chromatic-chroma-900) calc(var(--chromatic-hue) + ${60})) 90%, oklch(90% 0 360))`
})

const colors = computed(() => {
  return [primaryColor.value, secondaryColor.value, tertiaryColor.value, isDark.value ? '#171717' : '#FFFFFF']
})

const isRetryingStorage = ref(false)

async function retryContactStorage() {
  if (isRetryingStorage.value)
    return
  isRetryingStorage.value = true
  try {
    await contactsStore.retryLoad()
    if (!storageError.value) {
      const hadExistingContacts = contactsStore.contactsCount > 0
      contactsStore.migrateFromCards(cards.value, airiCardStore.activeCardId)
      await onboardingStore.initializeSetupCheck(hadExistingContacts)
      const memoryStore = useMemoryStore()
      memoryStore.setCurrentContact(activeContactId.value)
    }
  }
  finally {
    isRetryingStorage.value = false
  }
}

watch(settings.language, () => {
  i18n.locale.value = settings.language.value
})

watch(settings.themeColorsHue, () => {
  document.documentElement.style.setProperty('--chromatic-hue', settings.themeColorsHue.value.toString())
}, { immediate: true })

watch(settings.themeColorsHueDynamic, () => {
  document.documentElement.classList.toggle('dynamic-hue', settings.themeColorsHueDynamic.value)
}, { immediate: true })

// 根据联系人设置同步舞台模型选择
// 优先级：联系人 displayModelId > 角色卡 displayModelId > 第一个可用模型
function syncStageModelFromContact(contact: Contact | null): boolean {
  if (!contact)
    return false

  // 用户明确选择了不使用模型
  if (contact.displayModelId === null) {
    stageModelSelected.value = DISPLAY_MODEL_NONE
    return true
  }

  // 使用联系人指定的模型
  if (contact.displayModelId) {
    stageModelSelected.value = contact.displayModelId
    return true
  }

  // 未设置，尝试使用角色卡的模型
  const card = activeCard.value
  if (card?.metadata?.displayModelId) {
    stageModelSelected.value = String(card.metadata.displayModelId)
    return true
  }

  // fallback 到第一个可用模型
  if (displayModels.value.length > 0) {
    stageModelSelected.value = displayModels.value[0].id
    return true
  }

  return false
}

// 监听联系人切换，自动更新显示模型（延迟加载，避免快速切换时重复加载）
let stageModelUpdateTimer: ReturnType<typeof setTimeout> | null = null

watch(activeContact, (newContact) => {
  // 快速切换时取消上一次的延迟加载
  if (stageModelUpdateTimer) {
    clearTimeout(stageModelUpdateTimer)
    stageModelUpdateTimer = null
  }

  if (!newContact)
    return

  // 延迟 150ms 执行模型加载，让 UI 先响应
  stageModelUpdateTimer = setTimeout(async () => {
    stageModelUpdateTimer = null
    if (syncStageModelFromContact(newContact)) {
      await settingsStore.updateStageModel()
    }
  }, STAGE_MODEL_UPDATE_DELAY)
}, { immediate: false })

// Initialize first-time setup check when app mounts
onMounted(async () => {
  syncBaiduReauthorizationToast(needsReauthorization.value)

  // ⚡ 优化：并行加载，不阻塞
  const [contactsResult, displayModelsResult] = await Promise.allSettled([
    contactsStore.waitForLoad(),
    displayModelsStore.loadDisplayModelsFromIndexedDB(),
  ])

  // 处理加载失败
  if (contactsResult.status === 'rejected') {
    console.error('联系人数据加载失败:', contactsResult.reason)
    // 降级：使用空数据继续启动
  }

  if (displayModelsResult.status === 'rejected') {
    console.error('显示模型加载失败:', displayModelsResult.reason)
  }

  // 快速初始化关键路径
  if (contactsResult.status === 'fulfilled') {
    if (!storageError.value) {
      const hadExistingContacts = contactsStore.contactsCount > 0
      contactsStore.migrateFromCards(cards.value, airiCardStore.activeCardId)
      await onboardingStore.initializeSetupCheck(hadExistingContacts)
    }
    syncStageModelFromContact(activeContact.value)

    const memoryStore = useMemoryStore()
    if (activeContactId.value && !storageError.value) {
      memoryStore.setCurrentContact(activeContactId.value)
    }
  }

  // ⚡ 优化：数据加载完成后才标记应用就绪
  window.__appReady?.()

  // ⚡ 优化：延迟非关键操作（舞台模型加载）
  const scheduleIdleTask = (callback: () => void) => {
    if (typeof requestIdleCallback !== 'undefined') {
      requestIdleCallback(callback)
    }
    else {
      setTimeout(callback, 1)
    }
  }

  scheduleIdleTask(() => {
    syncStageModelFromContact(activeContact.value)
    settingsStore.initializeStageModel()
  })
})

onUnmounted(() => {
  dispose()
})

// Handle first-time setup events
function handleSetupConfigured() {
  onboardingStore.markSetupCompleted()
}

function handleSetupSkipped() {
  onboardingStore.markSetupSkipped()
}
</script>

<template>
  <ActivationGate>
    <StageTransitionGroup
      :primary-color="primaryColor"
      :secondary-color="secondaryColor"
      :tertiary-color="tertiaryColor"
      :colors="colors"
      :z-index="100"
      :disable-transitions="settings.disableTransitions.value"
      :use-page-specific-transitions="settings.usePageSpecificTransitions.value"
    >
      <div
        v-if="storageError"
        role="alert"
        class="mx-auto my-3 max-w-3xl w-full flex items-center justify-between gap-3 border border-amber-300 rounded-lg bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100"
      >
        <span>本地联系人数据暂时无法读取，系统已暂停保存以保护原数据。请重试加载后再继续。</span>
        <button
          class="shrink-0 border border-amber-400 rounded-md px-3 py-1.5 text-xs dark:border-amber-700 disabled:opacity-50"
          :disabled="isRetryingStorage"
          @click="retryContactStorage"
        >
          {{ isRetryingStorage ? '正在重试...' : '重试加载' }}
        </button>
      </div>

      <RouterView v-slot="{ Component }">
        <ErrorBoundary ref="errorBoundaryRef">
          <Transition name="layout-fade" mode="out-in">
            <KeepAlive :include="['IndexScenePage', 'StageScenePage']">
              <component :is="Component" :key="currentRoute.meta.layout || 'default'" />
            </KeepAlive>
          </Transition>
        </ErrorBoundary>
      </RouterView>
    </StageTransitionGroup>

    <Teleport to="body">
      <ToasterRoot @close="(id: string) => toast.dismiss(id)">
        <Toaster
          :position="isMobile ? 'top-center' : 'bottom-right'"
          :mobile-offset="{ top: 'calc(env(safe-area-inset-top, 0px) + 12px)', left: '12px', right: '12px' }"
        />
      </ToasterRoot>
    </Teleport>

    <!-- First Time Setup Dialog -->
    <OnboardingDialog
      v-model="shouldShowSetup"
      @configured="handleSetupConfigured"
      @skipped="handleSetupSkipped"
    />

    <!-- AI通话UI组件 -->
    <IncomingCallDialog />
    <OutgoingCallDialog />
    <CallModal />

    <!-- 全局确认弹窗宿主：供工具/store 等非组件上下文弹出 MeowChat 风格确认框 -->
    <GlobalConfirmDialog />
  </ActivationGate>
</template>

<style>
/* We need this to properly animate the CSS variable */
@property --chromatic-hue {
 syntax: '<number>';
 initial-value: 0;
 inherits: true;
}

@keyframes hue-anim {
 from {
 --chromatic-hue: 0;
 }
 to {
 --chromatic-hue: 360;
 }
}

.dynamic-hue {
 animation: hue-anim 10s linear infinite;
}

/* 布局切换淡入淡出 */
.layout-fade-enter-active,
.layout-fade-leave-active {
 transition: opacity 0.2s ease;
}

.layout-fade-enter-from,
.layout-fade-leave-to {
 opacity: 0;
}

/* 根元素样式统一由 styles/main.css 管理，请勿在此重复定义 */
</style>
